import { useQuery } from '@apollo/client';
import moment from 'moment-timezone';
import { useLayoutEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useInterstitialStatus } from 'hooks/useInterstitialStatus';
import { usePatient } from 'hooks/usePatient/usePatient';
import { BugTracker } from 'kb-shared/utilities/bugTracker';
import { mappedTimeZone, MOMENT_TIME_ZONES } from 'kb-shared/utilities/momentHelpers';
import {
  TIMESLOTS_AVAILABLE_QUERY,
  TimeSlotsAvailableQueryResponse
} from 'screens/Book/components/BookingCalendarTime/graphql/queries';
import { useHideInClinicNpvSlots } from 'screens/Book/steps/Time/useHideInClinicNpvSlots';
import { getMonthStartDateTime } from 'utilities/formatDate';
import { RoutesPath } from 'utilities/pageUrl';

import { useInsuranceStepCompletedOnInitialLoad } from './useInsuranceCompletedOnInitialLoad';
import { useLoadEntities } from './useLoadEntities';
import { usePreselectedClinic } from './usePreselectedClinic';
import { StepType } from '../types/types';
import { BookingSearchParams } from '../utils/bookingSearchParams';
import {
  getAllowedBookingStep,
  getFirstIncompleteBookingStep
} from '../utils/getFirstIncompleteBookingStep';
import { getAppointmentType } from '../utils/getters';
import { shouldSkipLocationStep } from '../utils/shouldSkipLocationStep';
import { containsOnlyVirtualAppointmentType, isSupportedStep } from '../utils/validation';

export const useBookingState = () => {
  const location = useLocation();
  const bookingParams = useMemo(() => new BookingSearchParams(location.search), [location.search]);
  const {
    status,
    loading: loadingInterstitial,
    error: interstitialError
  } = useInterstitialStatus();
  const insuranceStepCompletedInitially = useInsuranceStepCompletedOnInitialLoad();
  const consentStepCompleted = !status?.active || Boolean(status?.hasRequiredConsents);
  const partnerStepCompleted =
    !status?.active || Boolean(status?.skipPartnerDataCapture || status?.partnerDataCollected);
  const insuranceStepCompleted = !status?.active || Boolean(status?.insuranceDataCompleted);
  const firstIncompleteBookingStep = getFirstIncompleteBookingStep({
    consentStepCompleted,
    partnerStepCompleted,
    insuranceStepCompleted
  });
  const completedSteps: Array<StepType> = [];

  if (consentStepCompleted) completedSteps.push('consents');
  if (partnerStepCompleted) completedSteps.push('partner');
  if (insuranceStepCompletedInitially) completedSteps.push('insurance');

  const normalizedBookingParams = bookingParams.toNormalizedBookingParams();
  const entities = useLoadEntities(normalizedBookingParams);
  const { loadedEntities } = entities;

  // Preselect clinic for certain scenarios
  const {
    clinic: preselectedClinic,
    loading: loadingPreselectedClinic,
    error: preselectedClinicError
  } = usePreselectedClinic({
    clinicId: normalizedBookingParams.clinicId ?? undefined,
    isOnlyVirtual: containsOnlyVirtualAppointmentType(loadedEntities.allAppointmentTypes ?? []),
    selectedLab: loadedEntities.selectedLab,
    selectedProduct: loadedEntities.selectedProduct
  });

  if (preselectedClinic) {
    loadedEntities.selectedClinic = preselectedClinic;
  }

  const { patient, isLoggedIn } = usePatient();
  const preferredTimezone = patient?.preferredTimezone as keyof typeof MOMENT_TIME_ZONES;
  const labTimezone = patient?.lab?.timeZone;

  const date = bookingParams.get('date');
  const startDateTime = date
    ? `${date}T00:00:00.000Z`
    : getMonthStartDateTime(moment.tz(mappedTimeZone(preferredTimezone || labTimezone || 'UTC')));
  const endDateTime = date ? `${date}T23:59:59.999Z` : undefined;

  const selectedAppointmentTypes = loadedEntities.allAppointmentTypes ?? [];

  const inClinicAppointmentType = selectedAppointmentTypes.find(sat => !sat.virtual);

  const { data, loading } = useQuery<TimeSlotsAvailableQueryResponse>(TIMESLOTS_AVAILABLE_QUERY, {
    variables: {
      appointmentTypeId: Number(inClinicAppointmentType?.id),
      labId: normalizedBookingParams.labId,
      startDateTime: startDateTime,
      ...(endDateTime && { endDateTime: endDateTime })
    },
    onError: error => BugTracker.notify(error, 'AppointmentTimeSlotsAvailableQuery'),
    skip: !inClinicAppointmentType
  });

  const { hideInClinicSlots: hideInClinicNpvSlots, pending: loadingMarketStatus } =
    useHideInClinicNpvSlots(getAppointmentType(loadedEntities.selectedProduct));

  // Check if we have all data needed to determine if location step should be skipped
  const isDataReadyForSkipCheck =
    !loading && !loadingMarketStatus && !entities.loading && !!entities.loadedEntities;

  // Calculate skip location step: always returns boolean (never undefined)
  // - For anonymous users: always false (never skip)
  // - For logged-in users: calculate based on business logic when data is ready, otherwise false
  const skipLocationStep =
    isLoggedIn && isDataReadyForSkipCheck
      ? shouldSkipLocationStep({
          step: bookingParams.get('step') as StepType,
          selectedAppointmentTypes,
          selectedClinic: loadedEntities.selectedClinic,
          partnerClinicSearch: bookingParams.get('partner_clinic_search') ?? '',
          inClinicTimeSlotsExist: !!data?.hasTimeSlots,
          virtualOnlyMarket: hideInClinicNpvSlots
        })
      : false;

  const stepSupported = isSupportedStep(bookingParams.get('step') ?? '');

  // Silently update URL when location step should be skipped (no navigation/re-render)
  // Use useLayoutEffect to run synchronously before browser paint
  useLayoutEffect(() => {
    if (!isLoggedIn || !skipLocationStep) return;

    // Read from actual browser URL, not React Router's location (which doesn't update with replaceState)
    const currentParams = new BookingSearchParams(window.location.search);
    if (currentParams.get('step') !== 'location') return;

    currentParams.set('step', 'time');
    const newUrl = `${RoutesPath.BOOKING_BOOK}?${currentParams}`;
    window.history.replaceState(null, '', newUrl);
  }, [skipLocationStep, isLoggedIn, location.search]);

  useLayoutEffect(() => {
    if (!isLoggedIn || loadingInterstitial || !status) return;

    const currentParams = new BookingSearchParams(window.location.search);
    const currentStep = currentParams.get('step');
    const allowedBookingStep = getAllowedBookingStep({
      firstIncompleteBookingStep,
      requestedStep: currentStep
    });

    if (!allowedBookingStep || currentStep === allowedBookingStep) return;

    currentParams.set('step', allowedBookingStep);
    const newUrl = `${RoutesPath.BOOKING_BOOK}?${currentParams}`;
    window.history.replaceState(null, '', newUrl);
  }, [firstIncompleteBookingStep, isLoggedIn, loadingInterstitial, location.search, status]);

  const requestedStep = bookingParams.get('step');
  const calculatedStep =
    requestedStep === 'location' && skipLocationStep
      ? 'time'
      : getAllowedBookingStep({
          firstIncompleteBookingStep,
          requestedStep
        });

  return {
    completedSteps,
    entities: entities.loadedEntities,
    loading:
      entities.loading ||
      loadingInterstitial ||
      loadingPreselectedClinic ||
      loading ||
      loadingMarketStatus,
    error: entities.error || interstitialError || preselectedClinicError,
    consentStepCompleted,
    partnerStepCompleted,
    insuranceStepCompleted,
    firstIncompleteBookingStep,
    stepSupported,
    calculatedStep,
    bookingParams,
    hideInClinicNpvSlots
  };
};
